import { ReactElement } from "react";
import { Helmet } from "react-helmet-async";
import {
  BusinessCardData,
  ProcessData,
  UserData,
} from "../types/BusinessCardTypes";
import { getDisplayName } from "./processData";
import { plainTextOf } from "./richText";

const defaultImage =
  "https://cdn.1cdn.app/application/UMD/2405110558455_logo.png";

const friendlyProgramNames: Record<string, string> = {
  POINT_CARD: "Points Program",
  HIST_MESSAGE_CARD: "History Rewards",
  PERCENTAGE_CARD: "Percentage Rewards",
  INCREMENTAL_CARD: "Stamp Card",
  AMOUNT_CARD: "Amount Rewards",
  DEADLINE_CARD: "Limited Time Offers",
  VOLUME_CARD: "Volume Rewards",
};

interface BusinessCardHelmetProps {
  userData: UserData;
  businessCardData: BusinessCardData;
  entityData: ProcessData["entity_data"] | null | undefined;
  processId: string | undefined;
}

interface LoyaltyProgramHelmetProps {
  processData: ProcessData;
  processId: string | undefined;
}

interface CustomRegistrationHelmetProps {
  processData: ProcessData;
  processId: string | undefined;
}

interface EventHelmetProps {
  processData: ProcessData;
  processId: string | undefined;
}

export const generateBusinessCardHelmet = ({
  userData,
  businessCardData,
  entityData,
  processId,
}: BusinessCardHelmetProps): ReactElement => {
  const organizationName = entityData?.entity_name;
  const displayName = getDisplayName(userData);
  const hasName = userData.first_name && userData.last_name;

  const businessCardDescription = `Digital business card${
    hasName ? ` for ${displayName}` : ""
  } - ${businessCardData.jobTitle}${
    organizationName ? ` at ${organizationName}` : ""
  }. Digital business card with contact information.`;

  const sanitizedDescription =
    plainTextOf(businessCardData.description).substring(0, 100) ||
    "Digital business card with contact information and links.";

  const primaryOgImage =
    userData.avatar || entityData?.entity_logo || defaultImage;
  const primaryOgAlt = userData.avatar
    ? `${displayName} profile picture`
    : entityData?.entity_logo
    ? `${organizationName || "Super User Manager"} logo`
    : "Super User Manager";
  const secondaryOgImage =
    userData.avatar && entityData?.entity_logo
      ? entityData.entity_logo
      : undefined;
  const secondaryOgAlt = organizationName
    ? `${organizationName} logo`
    : "Business logo";
  const twitterImage =
    userData.avatar || entityData?.entity_logo || defaultImage;
  const canonicalUrl = `${process.env.REACT_APP_HOST_URL}/business-card/${processId}`;

  return (
    <Helmet>
      <title>{displayName} - Super User Manager</title>
      <meta name="description" content={businessCardDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="contact-card" content="true" />
      {hasName && <meta name="person-name" content={displayName} />}
      <meta name="person-email" content={userData.email} />
      {userData.phone && <meta name="person-phone" content={userData.phone} />}
      <meta name="person-job-title" content={businessCardData.jobTitle} />
      {organizationName && (
        <meta name="person-organization" content={organizationName} />
      )}

      <meta property="og:type" content="profile" />
      <meta
        property="og:title"
        content={`${displayName} - ${businessCardData.jobTitle}`}
      />
      <meta
        property="og:description"
        content={`Connect with ${displayName}${
          organizationName ? ` at ${organizationName}` : ""
        }. ${sanitizedDescription}`}
      />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Super User Manager" />
      <meta property="og:image" content={primaryOgImage} />
      <meta property="og:image:secure_url" content={primaryOgImage} />
      <meta property="og:image:alt" content={primaryOgAlt} />
      {userData.avatar && primaryOgImage === userData.avatar && (
        <>
          <meta property="og:image:width" content="400" />
          <meta property="og:image:height" content="400" />
        </>
      )}
      {secondaryOgImage && (
        <>
          <meta property="og:image" content={secondaryOgImage} />
          <meta property="og:image:secure_url" content={secondaryOgImage} />
          <meta property="og:image:alt" content={secondaryOgAlt} />
        </>
      )}
      {hasName && (
        <meta property="profile:first_name" content={userData.first_name} />
      )}
      {hasName && (
        <meta property="profile:last_name" content={userData.last_name} />
      )}

      <meta name="twitter:card" content="summary" />
      <meta
        name="twitter:title"
        content={`${displayName} - ${businessCardData.jobTitle}`}
      />
      <meta
        name="twitter:description"
        content={`Connect with ${displayName}${
          organizationName ? ` at ${organizationName}` : ""
        }. Digital business card with contact information.`}
      />
      <meta name="twitter:image" content={twitterImage} />

      <meta name="author" content={displayName} />
      <meta
        name="organization"
        content={organizationName || "Super User Manager"}
      />
      <meta name="contact:email" content={userData.email} />
      {userData.phone && (
        <meta name="contact:phone_number" content={userData.phone} />
      )}

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: displayName,
          jobTitle: businessCardData.jobTitle,
          email: userData.email,
          telephone: userData.phone || undefined,
          image: userData.avatar || defaultImage,
          url: canonicalUrl,
          ...(organizationName && {
            worksFor: {
              "@type": "Organization",
              name: organizationName,
              description: entityData?.entity_description || "",
              logo: entityData?.entity_logo || defaultImage,
            },
          }),
        })}
      </script>
    </Helmet>
  );
};

export const generateLoyaltyProgramHelmet = ({
  processData,
  processId,
}: LoyaltyProgramHelmetProps): ReactElement => {
  const entityName = processData?.entity_data?.entity_name || "Business";
  const entityDesc = processData?.entity_data?.entity_description;
  const entityLogo = processData?.entity_data?.entity_logo || defaultImage;
  const comment = processData?.process_data?.comment;
  const lmcType = processData?.process_data?.lmc_type || "LOYALTY_PROGRAM";

  const friendlyType = friendlyProgramNames[lmcType] || "Loyalty Program";

  const description = (() => {
    if (comment?.trim()) {
      return `${comment.substring(
        0,
        150
      )}... Join ${entityName}'s loyalty program and earn rewards.`;
    }

    if (entityDesc?.trim()) {
      return `Join ${entityName}'s loyalty program. ${entityDesc.substring(
        0,
        120
      )}... Earn rewards with every visit.`;
    }

    return `Join ${entityName}'s loyalty program and earn rewards with every visit. Mobile-friendly digital loyalty card.`;
  })();

  const canonicalUrl = `${process.env.REACT_APP_HOST_URL}/loyalty-program/${processId}`;
  const title = `${entityName} - ${friendlyType}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="application-name" content="Loyalty Program By SUM" />
      <meta
        name="keywords"
        content={`loyalty program, Super User Manager, ${friendlyType}, ${entityName}`}
      />
      <meta name="author" content={entityName} />
      {entityDesc && <meta name="business-description" content={entityDesc} />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Super User Manager" />
      <meta property="og:image" content={entityLogo} />
      <meta property="og:image:secure_url" content={entityLogo} />
      <meta property="og:image:alt" content={`${entityName} logo`} />

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={entityLogo} />

      <meta name="organization" content={entityName} />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProgramMembership",
          name: title,
          description: description,
          url: canonicalUrl,
          provider: {
            "@type": "Organization",
            name: entityName,
            description: entityDesc || "",
            logo: entityLogo,
          },
          programType: lmcType,
        })}
      </script>
    </Helmet>
  );
};

export const generateCustomRegistrationHelmet = ({
  processData,
  processId,
}: CustomRegistrationHelmetProps): ReactElement => {
  const entityName = processData?.entity_data?.entity_name || "Business";
  const entityDesc = processData?.entity_data?.entity_description;
  const entityLogo = processData?.entity_data?.entity_logo || defaultImage;

  const description = entityDesc?.trim()
    ? `Sign up for ${entityName}. ${entityDesc.substring(0, 120)}...`
    : `Sign up and register with ${entityName}. Quick and easy registration form.`;

  const canonicalUrl = `${process.env.REACT_APP_HOST_URL}/custom-registration/${processId}`;
  const title = `Sign Up - ${entityName}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="application-name" content="Sign-Up Form By SUM" />
      <meta
        name="keywords"
        content={`sign up, registration, ${entityName}, Super User Manager`}
      />
      <meta name="author" content={entityName} />
      {entityDesc && <meta name="business-description" content={entityDesc} />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Super User Manager" />
      <meta property="og:image" content={entityLogo} />
      <meta property="og:image:secure_url" content={entityLogo} />
      <meta property="og:image:alt" content={`${entityName} logo`} />

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={entityLogo} />

      <meta name="organization" content={entityName} />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description: description,
          url: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: entityName,
            description: entityDesc || "",
            logo: entityLogo,
          },
        })}
      </script>
    </Helmet>
  );
};

export const generateEventHelmet = ({
  processData,
  processId,
}: EventHelmetProps): ReactElement => {
  const entityName = processData?.entity_data?.entity_name || "Business";
  const entityDesc = processData?.entity_data?.entity_description;
  const entityLogo = processData?.entity_data?.entity_logo || defaultImage;

  const eventCode = processData?.process_data?.event_code;
  const events = processData?.process_data?.events;
  const eventDetails = eventCode && events ? events[eventCode] : null;
  const eventSettings = eventDetails?.event_data?.settings;

  const eventName = eventSettings?.event_name || "Event";
  const eventDescription = eventSettings?.event_description;
  const eventImageUrl = eventSettings?.event_image_url || entityLogo;
  const eventLocation = eventSettings?.event_location;
  const eventStartDate = eventSettings?.event_start_date;
  const eventEndDate = eventSettings?.event_end_date;

  const description = eventDescription?.trim()
    ? `${eventDescription.substring(0, 150)}... ${
        eventLocation ? `Location: ${eventLocation}.` : ""
      } Organized by ${entityName}.`
    : `Join ${eventName} organized by ${entityName}. ${
        eventLocation ? `Location: ${eventLocation}.` : ""
      } Get your tickets now!`;

  const canonicalUrl = `${process.env.REACT_APP_HOST_URL}/event/${processId}`;
  const title = `${eventName} - ${entityName}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="application-name" content="Events By SUM" />
      <meta
        name="keywords"
        content={`event, tickets, ${eventName}, ${entityName}, Super User Manager`}
      />
      <meta name="author" content={entityName} />

      <meta name="color-scheme" content="light only" />
      <meta name="supported-color-schemes" content="light only" />
      <meta name="nightmode" content="disable" />
      {entityDesc && <meta name="business-description" content={entityDesc} />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Super User Manager" />
      <meta property="og:image" content={eventImageUrl} />
      <meta property="og:image:secure_url" content={eventImageUrl} />
      <meta property="og:image:alt" content={`${eventName} image`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={eventImageUrl} />

      <meta name="organization" content={entityName} />
      {eventLocation && <meta name="event:location" content={eventLocation} />}
      {eventStartDate && (
        <meta name="event:start_date" content={eventStartDate} />
      )}
      {eventEndDate && <meta name="event:end_date" content={eventEndDate} />}

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          name: eventName,
          description: eventDescription || description,
          url: canonicalUrl,
          image: eventImageUrl,
          ...(eventStartDate && { startDate: eventStartDate }),
          ...(eventEndDate && { endDate: eventEndDate }),
          ...(eventLocation && {
            location: {
              "@type": "Place",
              name: eventLocation,
            },
          }),
          organizer: {
            "@type": "Organization",
            name: entityName,
            description: entityDesc || "",
            logo: entityLogo,
          },
          offers: {
            "@type": "Offer",
            url: canonicalUrl,
            availability: "https://schema.org/InStock",
          },
        })}
      </script>
    </Helmet>
  );
};
